import { useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BasicResponsiveImage, Box } from '@vp/swan';
import { useStyles } from '@vp/ubik-context';
import styles, { stylesheet } from './ParallaxItem.module.scss';

type ParallaxItemId =
    | 'TShirt'
    | 'Cap'
    | 'DogCollar'
    | 'BusinessCards'
    | 'Font'
    | 'Flask'
    | 'AFrameSign'
    | 'Ball';

const BREAKPOINTS = {
    xs: 767, // max-width: 767px
    sm: 1023, // max-width: 1023px
    md: 1439, // max-width: 1439px
    // lg: everything above 1439px (default)
} as const;

type Breakpoint = 'xs' | 'sm' | 'md' | 'lg';

type CalloutBaseline = { baselineWidth: number; anchor: 'left' | 'right' };

// Baseline callout widths (px) measured from the EN locale with the Boyrun font.
// Only items that render a callout (children) need an entry here.
// `anchor` indicates which CSS edge (`left` or `right`) positions the callout,
// so we know which direction to compensate when the rendered width differs.
const EN_CALLOUT_BASELINES: Partial<
    Record<ParallaxItemId, Partial<Record<Breakpoint, CalloutBaseline>> & { lg: CalloutBaseline }>
> = {
    TShirt: {
        lg: { baselineWidth: 150, anchor: 'left' },
        sm: { baselineWidth: 140, anchor: 'left' },
        xs: { baselineWidth: 180, anchor: 'left' },
    },
    Cap: {
        lg: { baselineWidth: 150, anchor: 'left' },
        // Hidden at sm & xs
    },
    BusinessCards: {
        lg: { baselineWidth: 130, anchor: 'left' },
        // Hidden at md, sm & xs
    },
    Flask: {
        lg: { baselineWidth: 90, anchor: 'right' },
        // Hidden at sm & xs
    },
};

const getActiveBreakpoint = (): Breakpoint => {
    if (typeof window === 'undefined') return 'lg';
    const width = window.innerWidth;
    if (width <= BREAKPOINTS.xs) return 'xs';
    if (width <= BREAKPOINTS.sm) return 'sm';
    if (width <= BREAKPOINTS.md) return 'md';
    return 'lg';
};

const resolveBaseline = (id: ParallaxItemId): CalloutBaseline | undefined => {
    const entry = EN_CALLOUT_BASELINES[id];
    if (!entry) return undefined;

    const bp = getActiveBreakpoint();
    // Fall back through breakpoints to `lg` if a specific override is not defined.
    return entry[bp] ?? entry.lg;
};

// Divisor used when computing the horizontal compensation.
// A value of 2 would centre-correct exactly; 1.15 was tuned visually
// to account for the callout arrow offset and rotation.
const SHIFT_DIVISOR = 1.15;

const ParallaxItem = ({
    id,
    url,
    aspectRatio,
    altText,
    scrollOffset = 0,
    parallaxSpeed = 0,
    children,
}: {
    id: ParallaxItemId;
    url: string;
    aspectRatio: number;
    altText: string;
    scrollOffset?: number;
    parallaxSpeed?: number;
    children?: React.ReactNode;
}) => {
    const parallaxOffset = scrollOffset * parallaxSpeed;
    useStyles(stylesheet);
    const { t } = useTranslation();
    const calloutRef = useRef<HTMLDivElement>(null);

    // Compensate callout position for text that is wider/narrower than the EN baseline.
    // Sets a CSS custom property (--callout-shift) consumed by translateX() in the SCSS,
    // so each item's original left/right/rotation values stay untouched.
    useLayoutEffect(() => {
        const calloutElement = calloutRef.current;
        if (!calloutElement) return;

        const applyShiftCompensation = () => {
            const baseline = resolveBaseline(id);

            // No baseline for this item — reset any previously applied shift and bail out.
            if (!baseline) {
                calloutElement.style.setProperty('--callout-shift', '0px');
                return;
            }

            // A baselineWidth of 0 means the callout is hidden at this breakpoint.
            if (baseline.baselineWidth === 0) {
                calloutElement.style.setProperty('--callout-shift', '0px');
                return;
            }

            const renderedWidth = calloutElement.offsetWidth;
            if (renderedWidth === 0) {
                // Element is hidden at this breakpoint; reset any previous compensation.
                calloutElement.style.setProperty('--callout-shift', '0px');
                return;
            }

            const widthDelta = (renderedWidth - baseline.baselineWidth) / SHIFT_DIVISOR;

            // Left-anchored: wider text grows rightward → shift left (negative) to compensate.
            // Right-anchored: wider text grows leftward → shift right (positive) to compensate.
            const compensationPx = baseline.anchor === 'left' ? -widthDelta : widthDelta;
            calloutElement.style.setProperty('--callout-shift', `${compensationPx}px`);
        };

        // Cache the current breakpoint so we can skip recalculation when it
        // hasn't changed.  This avoids unnecessary work during continuous
        // resizing within the same breakpoint range.
        let cachedBreakpoint: Breakpoint = getActiveBreakpoint();

        applyShiftCompensation();

        // Re-run when the element resizes (e.g. custom font finishes loading, viewport change).
        const resizeObserver = new ResizeObserver(applyShiftCompensation);
        resizeObserver.observe(calloutElement);

        // Debounced window resize handler — only needed to pick up breakpoint
        // crossings that don't trigger a ResizeObserver callback on the callout
        // element itself.  A 150 ms delay avoids firing on every pixel change.
        let resizeTimer: ReturnType<typeof setTimeout> | undefined;
        const debouncedResize = () => {
            if (resizeTimer !== undefined) {
                clearTimeout(resizeTimer);
            }
            resizeTimer = setTimeout(() => {
                const newBreakpoint = getActiveBreakpoint();
                if (newBreakpoint !== cachedBreakpoint) {
                    cachedBreakpoint = newBreakpoint;
                    applyShiftCompensation();
                }
            }, 150);
        };
        window.addEventListener('resize', debouncedResize);

        return () => {
            resizeObserver.disconnect();
            if (resizeTimer !== undefined) {
                clearTimeout(resizeTimer);
            }
            window.removeEventListener('resize', debouncedResize);
            if (calloutElement && calloutElement.isConnected) {
                calloutElement.style.removeProperty('--callout-shift');
            }
        };
    }, [id]);

    return (
        <Box
            style={{
                transform: `translate3d(0, ${parallaxOffset}px, 0)`,
                willChange: 'transform',
            }}
            className={`${styles.parallaxItem} ${styles[`parallaxItem${id}` as keyof typeof styles]}`}
        >
            <BasicResponsiveImage aspectRatio={aspectRatio} alt={altText} src={url} />
            {children && (
                <Box ref={calloutRef} className={styles.parallaxItemCallout}>
                    {children}
                    <Box className={styles.parallaxItemCalloutArrow}>
                        <BasicResponsiveImage
                            aspectRatio={aspectRatio}
                            alt={t(
                                'Logomaker GenAi Landing Page > Logo To Life Showcase > Image 9.altText',
                            )}
                            src={t(
                                'Logomaker GenAi Landing Page > Logo To Life Showcase > Image 9.src',
                            )}
                        />
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default ParallaxItem;
